// The navigational lifecycle's BUILD half: installing one database identity's
// index plus its DDL-invalidator registration, running (and re-running) the
// catalog build behind it, and the deferred connect-time schedule that does
// both for the connection's configured database.
//
// Split out of `navigationalLifecycle.ts` (which keeps the architectural
// overview, and the on-demand `indexReadyFor` path that calls in here).
// Everything below runs only once the case policy is known
// (`navLifecycleDatabaseResolution.ts`), because every key it computes depends
// on it.

import { objectKeyCaseFor } from './navCasePolicy';
import { databaseIdentityKey } from './databaseIdentity';
import { navIndexUnavailable } from './navIndexUnavailable';
import type { SnapshotWaitTarget } from './navIndexSchedule';
import {
  isOrdinaryDisconnect,
  liveLifecycle,
  type ConnectionEntry,
  type DatabaseIndexEntry,
} from './navLifecycleRegistry';
import { ensureFullCasePolicyKnown, routingDatabaseFor } from './navLifecycleDatabaseResolution';

/**
 * The connect-time schedule for the connection's configured database: both
 * gates, then the case policy, and only THEN the index + registration + build.
 * Nothing waits on this one, so it never kicks the gates — an `indexReadyFor`
 * that arrives first creates the entry itself (skipping the gates, because a
 * user is waiting) and this schedule then finds it and does nothing.
 */
export async function scheduleConfiguredDatabase(
  connectionId: string,
  entry: ConnectionEntry,
): Promise<void> {
  const { deps, engineId } = entry;
  const isLive = (): boolean => liveLifecycle.get(connectionId) === entry;
  try {
    // The SNAPSHOT wait keys on the connection's own database spelling — `''`
    // included, which is how the IntelliSense caches file an unconfigured
    // connection's snapshot.
    const target: SnapshotWaitTarget = { connectionId, database: entry.defaultDatabase, engineId };
    await deps.waitForSchemaSnapshot(target);
    if (!isLive()) return;
    await deps.waitForIdleMoment();
    if (!isLive()) return;
    // NOT `entry.defaultDatabase`: the gate is where an unconfigured connection
    // learns the login default's real name, and that name is the identity the
    // first Object-Explorer click will ask for explicitly.
    const database = await ensureFullCasePolicyKnown(connectionId, entry, undefined);
    if (!isLive()) return;
    // An on-demand request may have built this identity while we waited.
    if (entry.databases.has(databaseIdentityKey(connectionId, engineId, database))) return;
    // Started, not awaited: this schedule's job is to get the build going, and
    // the build reports its own outcome (`runBuild` logs a failure and marks
    // the index partial). Awaiting it here only re-logged the same failure
    // under a message that says it never started.
    startDatabaseIndex(connectionId, entry, database);
  } catch (err) {
    // A disconnect — or a reconnect of the same id — DURING the case-policy
    // probe is the ordinary case, not a fault: the `isLive()` checks above
    // would have returned quietly a microtask later, and the user closing a
    // connection while it is still settling should not leave a warning behind.
    // Anything else is unexpected and still says so.
    if (isOrdinaryDisconnect(err)) return;
    console.warn(
      '[nav-index] scheduled index for',
      connectionId,
      entry.defaultDatabase,
      'did not start',
      err,
    );
  }
}

/**
 * Install the index + invalidator registration for one database identity of a
 * connection and start its build. Only called once the connection's case
 * policy is known — every key below depends on it (see the module header of
 * `navigationalLifecycle.ts`).
 *
 * `database` is the database the request is ABOUT; `routed` is the one its
 * catalog queries RUN IN, which differs only where one index spans the whole
 * connection and the requester's name would merely route it somewhere the
 * connection may not be able to go (`routingDatabaseFor` holds the argument).
 * The two always name the SAME identity — that is the condition under which
 * the substitution is made — so the index key below, the per-database case
 * policy the gate already probed, and the DDL registration's own key are all
 * unaffected by which of them is used.
 */
export function startDatabaseIndex(
  connectionId: string,
  entry: ConnectionEntry,
  database: string,
): DatabaseIndexEntry {
  const { deps, engineId } = entry;
  const routed = routingDatabaseFor(connectionId, entry, database);
  const index = deps.createIdentifierIndex(connectionId, {
    caseSensitiveObjects: objectKeyCaseFor(connectionId, engineId, routed),
  });
  const disposeInvalidator = deps.registerDdlInvalidator({
    connectionId,
    database: routed,
    engineId,
    index,
    // Reads `dbEntry` (declared below) when the invalidator CALLS it, never
    // now. Every engine registers through here, so the deferral covers MySQL /
    // Oracle / SQLite's single whole-connection identity and SQL Server /
    // PostgreSQL's per-database ones alike.
    whenIdle: () => whenBuildSettled(dbEntry),
    isBuilding: () => isBuildingNow(dbEntry),
    buildGeneration: () => buildGenerationOf(dbEntry),
  });

  const dbEntry: DatabaseIndexEntry = {
    index,
    database: routed,
    // Replaced by `runBuild` on the next line — a `DatabaseIndexEntry` never
    // exists without a build behind it.
    ready: Promise.resolve(),
    buildFailed: false,
    cancelled: false,
    disposeInvalidator,
    cancel: () => {
      dbEntry.cancelled = true;
    },
  };
  dbEntry.ready = runBuild(connectionId, entry, dbEntry);
  entry.databases.set(databaseIdentityKey(connectionId, engineId, database), dbEntry);
  return dbEntry;
}

/**
 * The registration's "no build in flight" hook (`index/indexInvalidator.ts`'s
 * `whenIdle`): resolves at once when this identity's index is not being built,
 * and when the build in flight SETTLES otherwise.
 *
 * A DDL refresh that runs DURING a build records into an index the build is
 * still filling — and a RETRY empties that index first (`runBuild`), so the
 * refreshed object is then recorded a second time by the rebuild and listed
 * twice by every reader that does not dedupe. The refresh is therefore
 * DEFERRED behind this, not skipped: the build's catalog listing was fetched
 * before the statement ran, so the build cannot carry the change.
 *
 * SETTLED, not fulfilled: a FAILED build keeps the records it did write, so
 * the refresh still applies to them. And a retry REPLACES `ready`, so the wait
 * re-reads the field after each settle — releasing on the promise it started
 * with would drop the refresh into the middle of the build that replaced it.
 */
async function whenBuildSettled(dbEntry: DatabaseIndexEntry): Promise<void> {
  for (let pending = dbEntry.ready; ; pending = dbEntry.ready) {
    await pending.catch(() => undefined);
    if (dbEntry.ready === pending) return;
  }
}

/**
 * The entries with a build IN FLIGHT — the SYNCHRONOUS half of the hook above
 * (`IndexInvalidatorRegistration.isBuilding`), for the reverse ordering
 * `whenIdle` cannot close: a refresh that started while nothing was building
 * and is still awaiting its body fetch when a RETRY empties the index and
 * begins recording into it. The refresh re-checks this immediately before its
 * swap and re-queues instead of writing.
 *
 * PENDING means: from `runBuild`'s first statement — which is where the retry
 * `clear()`s, so no window is left in front of it — until its `ready` settles,
 * which the `finally` inside the build's own body guarantees happens BEFORE
 * `ready` does (a waiter released by `whenIdle` therefore never sees a stale
 * `true`). One build per entry at a time: the only retry path
 * (`indexReadyFor`) gates on `buildFailed`, which `runBuild` clears
 * synchronously.
 */
const buildingEntries = new WeakSet<DatabaseIndexEntry>();

/** How many builds this entry has STARTED. `isBuilding` alone cannot see a
 *  build that began AND finished inside one refresh's body fetch: the refresh
 *  reads `false` before and `false` after, and installs a body older than the
 *  records the build just wrote. A counter the refresh captures before its
 *  fetch and re-reads at its swap does see it. */
const buildGenerations = new WeakMap<DatabaseIndexEntry, number>();

function isBuildingNow(dbEntry: DatabaseIndexEntry): boolean {
  return buildingEntries.has(dbEntry);
}

function buildGenerationOf(dbEntry: DatabaseIndexEntry): number {
  return buildGenerations.get(dbEntry) ?? 0;
}

/**
 * Run (or re-run) one database's catalog build and hand back its readiness.
 *
 * Three outcomes, three different things to say:
 *   - SUCCESS — fulfils.
 *   - CANCELLED — teardown flipped `shouldAbort` and cleared this index, so the
 *     builder returned early and there is nothing behind this promise.
 *     Fulfilling it would hand the waiting References tab an emptied index —
 *     or, when the same id has already reconnected, the new lifecycle's
 *     not-yet-built one — as a finished search. Rejects `'cancelled'`.
 *   - FAILED — the catalog read rejected (a dropped IPC call, an engine error
 *     mid-paging). Whatever the builder recorded before it threw is a real
 *     subset of the catalog, so the index KEEPS it and stays installed (its DDL
 *     registration with it) — but it is marked PARTIAL, because "no references
 *     found" over a fraction of the catalog is the false negative this area
 *     exists to remove, and readiness rejects `'build-failed'` so the surface
 *     shows its error state instead. The entry remembers the failure and the
 *     next `indexReadyFor` rebuilds into the same index.
 */
export function runBuild(
  connectionId: string,
  entry: ConnectionEntry,
  dbEntry: DatabaseIndexEntry,
): Promise<void> {
  const { deps, engineId } = entry;
  const { database, index } = dbEntry;
  // A RETRY rebuilds into the SAME index object — its DDL registration points
  // at it (`indexInvalidator.ts` keyed the handle, not a copy) — and the
  // builder only RECORDS: it never clears. So the failed attempt's records have
  // to go first, or every object it reached is held TWICE and listed twice by
  // every reader that does not dedupe (`findReferencesService.ts`,
  // `peekReferenceLocations.ts`; only `renamePlanner.dedupeByRange` is
  // protected). `clear()` is also what drops the PARTIAL flag the failure set —
  // without it a COMPLETE rebuild still answers `isPartial() === true` and the
  // tab keeps warning about a fraction of the catalog it now covers in full —
  // and it releases the per-kind frozen key policy, which an empty index may
  // re-adopt from a policy that has since become known (`identifierIndex.ts`).
  // Before the `clear()` below, so a refresh can never slip a write into the
  // window between emptying the index and the first recorded row.
  buildingEntries.add(dbEntry);
  buildGenerations.set(dbEntry, buildGenerationOf(dbEntry) + 1);
  if (dbEntry.buildFailed) index.clear();
  dbEntry.buildFailed = false;
  const ready = (async () => {
    let failure: unknown;
    try {
      await deps.buildIndexForConnection(
        { connectionId, database, engineId, shouldAbort: () => dbEntry.cancelled },
        index,
      );
    } catch (err) {
      failure = err ?? new Error('nav index build rejected');
    } finally {
      // INSIDE the body: this runs before `ready` settles, so a refresh
      // released by `whenIdle` always reads `isBuilding() === false`.
      buildingEntries.delete(dbEntry);
    }
    if (dbEntry.cancelled) {
      throw navIndexUnavailable(
        'cancelled',
        `nav index build cancelled for connection ${connectionId}`,
      );
    }
    if (failure !== undefined) {
      index.markPartial();
      dbEntry.buildFailed = true;
      console.warn('[nav-index] build failed for', connectionId, database, failure);
      throw navIndexUnavailable(
        'build-failed',
        `nav index build failed for connection ${connectionId}: ${
          failure instanceof Error ? failure.message : String(failure)
        }`,
      );
    }
  })();
  // Nothing awaits the connect-time schedule's copy of this promise, and an
  // un-awaited rejection is reported as an unhandled one. Attaching a handler
  // here marks it handled WITHOUT consuming it: every real awaiter still sees
  // the rejection, because they await `ready` itself.
  void ready.catch(() => undefined);
  return ready;
}
